/** Tag loader — turns consent state into actual third-party scripts.
 *
 *  Subscribes to `br:consent-changed` (and runs once on first import) and:
 *    1. Loads analytics tags when `categories.analytics === true`.
 *    2. Loads marketing/advertising tags when `categories.marketing === true`.
 *    3. Removes the script element AND clears the corresponding cookies
 *       (first-party + common third-party patterns) when consent is revoked,
 *       so a user who toggles off actually stops being tracked from that
 *       moment — required for GDPR/PECR and ePrivacy compliance.
 *
 *  Tag IDs are read from frontend `.env` so the loader is dormant by default.
 *  Supported today (add more as you adopt them):
 *
 *    REACT_APP_PLAUSIBLE_DOMAIN   — Plausible analytics (cookie-free, but
 *                                   still gated behind analytics consent
 *                                   for jurisdictional consistency).
 *    REACT_APP_GA4_ID             — Google Analytics 4 (G-XXXXXXXX).
 *    REACT_APP_META_PIXEL_ID      — Meta/Facebook Pixel.
 */

import { readConsent } from "./cookieConsent";

const ATTR = "data-br-tag"; // marker so we can find + remove our tags later

/* ------------ helpers ------------ */

function injectScript({ id, src, async = true, defer = false, attrs = {} }) {
  if (document.querySelector(`script[${ATTR}="${id}"]`)) return; // already loaded
  const s = document.createElement("script");
  s.setAttribute(ATTR, id);
  s.src = src;
  s.async = async;
  s.defer = defer;
  Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
  document.head.appendChild(s);
}

function injectInline({ id, code }) {
  if (document.querySelector(`script[${ATTR}="${id}"]`)) return;
  const s = document.createElement("script");
  s.setAttribute(ATTR, id);
  s.text = code;
  document.head.appendChild(s);
}

function removeTags(ids) {
  ids.forEach((id) => {
    document.querySelectorAll(`script[${ATTR}="${id}"]`).forEach((el) => el.remove());
  });
}

/** Delete cookies that match a prefix on the current host AND `.host`. */
function clearCookies(prefixes) {
  const host = window.location.hostname;
  const domains = ["", host, "." + host, "." + host.split(".").slice(-2).join(".")];
  document.cookie.split(";").forEach((raw) => {
    const name = raw.split("=")[0].trim();
    if (!name) return;
    if (!prefixes.some((p) => name.startsWith(p))) return;
    domains.forEach((d) => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/${d ? `; domain=${d}` : ""}`;
    });
  });
}

/* ------------ analytics ------------ */

const PLAUSIBLE_DOMAIN = process.env.REACT_APP_PLAUSIBLE_DOMAIN;
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;
// NOTE: GA4 (G-41H03YPRXY) intentionally loads unconditionally from
// public/index.html per owner decision (2026-09) — it is NOT consent-gated.

function loadAnalytics() {
  if (PLAUSIBLE_DOMAIN) {
    injectScript({
      id: "plausible",
      src: "https://plausible.io/js/script.js",
      defer: true,
      attrs: { "data-domain": PLAUSIBLE_DOMAIN },
    });
  }
}

function unloadAnalytics() {
  removeTags(["plausible"]);
}

/* ------------ marketing ------------ */

function loadMarketing() {
  if (META_PIXEL_ID) {
    injectInline({
      id: "meta-pixel-init",
      code:
        // Standard Meta Pixel base code, gated and anonymised.
        "!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?" +
        "n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;" +
        "n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;" +
        "t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window," +
        "document,'script','https://connect.facebook.net/en_US/fbevents.js');" +
        `fbq('init', '${META_PIXEL_ID}');fbq('track', 'PageView');`,
    });
  }
}

function unloadMarketing() {
  removeTags(["meta-pixel-init"]);
  // Meta sets _fbp, _fbc.
  clearCookies(["_fbp", "_fbc"]);
  // Cleanly disable fbq if it loaded earlier this session.
  if (window.fbq) {
    try { window.fbq.disablePushState = true; } catch {}
  }
}

/* ------------ public ------------ */

let lastAnalytics = null;
let lastMarketing = null;

/** Apply a consent payload (or `null` for "no decision yet"). */
export function applyConsent(consent) {
  const analyticsOn = !!consent?.categories?.analytics;
  const marketingOn = !!consent?.categories?.marketing;

  if (analyticsOn !== lastAnalytics) {
    analyticsOn ? loadAnalytics() : unloadAnalytics();
    lastAnalytics = analyticsOn;
  }
  if (marketingOn !== lastMarketing) {
    marketingOn ? loadMarketing() : unloadMarketing();
    lastMarketing = marketingOn;
  }
}

/** Initialise once at app boot. Subscribes to consent changes. */
export function initTagLoader() {
  if (typeof window === "undefined") return;
  applyConsent(readConsent());
  window.addEventListener("br:consent-changed", (e) => applyConsent(e.detail));
}
